/// <reference lib="dom" />
import { clamp } from "@utils/utils";
import { ParamDict, ParamDictToProps } from "@utils/params";
import { G } from "./graphConstants.ts";
import { curvePathFrom, makeLinearScale, makeLogScale, sampleCurveByScale } from "./scaleUtils.ts";
import { appendBackgroundRect, appendFixedSvg, appendScaledSvg, SvgSelection } from "./d3Chart.ts";

//#region FreqSvgParams / FreqSvgOpts

export const FreqSvgParams = {
  "domain/fMin": { type: "float", value: 20 },
  "domain/fMax": { type: "float", value: 20000 },
  "domain/mMax": { type: "float", value: 1.5 },
  "domain/qMin": { type: "float", value: 0.1 },
  "domain/qMax": { type: "float", value: 10 },
  "editor/width": { type: "float", value: 12 * G },
  "editor/height": { type: "float", value: 4 * G },
  "editor/pad": { type: "float", value: Math.round(G / 4) },
  "inline/height": { type: "float", value: Math.round(G * 0.75) },
  "inline/pad": { type: "float", value: 1 },
  "inline/virtualWidth": { type: "float", value: 8 * G },
} satisfies ParamDict;
export type FreqSvgParams = typeof FreqSvgParams;
export const FreqSvgTicks = [50, 100, 200, 500, 1000, 5000, 10000];
export const FreqSvgOptsDefaults = {
  ...ParamDictToProps(FreqSvgParams),
  "domain/ticks": FreqSvgTicks,
};
export type FreqSvgOpts = typeof FreqSvgOptsDefaults;

//#endregion

//#region Math — pure, fully parameterized, no single-letter locals

// 2nd-order biquad prototype magnitude (continuous-time approximation)
export function filterMagnitude(type: string, fc: number, q: number, freq: number): number {
  const normFreq = freq / Math.max(fc, 1);
  const denom = Math.sqrt(
    Math.pow(1 - normFreq * normFreq, 2) +
      Math.pow(normFreq / Math.max(q, 0.01), 2),
  );
  switch (type) {
    case "lowpass":
      return 1 / denom;
    case "highpass":
      return (normFreq * normFreq) / denom;
    case "bandpass":
      return (normFreq / Math.max(q, 0.01)) / denom;
    case "notch":
      return Math.abs(1 - normFreq * normFreq) / denom;
    default:
      return 1;
  }
}

export function makeTx(width: number, pad: number, fMin: number, fMax: number) {
  const scale = makeLogScale([fMin, fMax], [pad, width - pad]);
  return (freq: number) => scale(freq);
}
export function makeFt(width: number, pad: number, fMin: number, fMax: number) {
  const scale = makeLogScale([fMin, fMax], [pad, width - pad]);
  return (xPos: number) => scale.invert(xPos);
}
export function makeVy(height: number, pad: number, mMax: number) {
  const scale = makeLinearScale([0, mMax], [height - pad, pad]);
  return (mag: number) => scale(mag);
}
export function qToY(q: number, height: number, pad: number, qMin: number, qMax: number): number {
  return makeLogScale([qMin, qMax], [height - pad, pad])(q);
}
export function yToQ(
  yPos: number,
  height: number,
  pad: number,
  qMin: number,
  qMax: number,
): number {
  return makeLogScale([qMin, qMax], [height - pad, pad]).invert(yPos);
}

export function buildResponsePath(
  type: string,
  fc: number,
  q: number,
  width: number,
  height: number,
  pad: number,
  opts: Pick<FreqSvgOpts, "domain/fMin" | "domain/fMax" | "domain/mMax">,
  nPoints = 80,
): string {
  const fMin = opts["domain/fMin"];
  const fMax = opts["domain/fMax"];
  const mMax = opts["domain/mMax"];
  const xScale = makeLogScale([fMin, fMax], [pad, width - pad]);
  const yScale = makeLinearScale([0, mMax], [height - pad, pad]);
  const points = sampleCurveByScale(
    (freq) => clamp(filterMagnitude(type, fc, q, freq), 0, mMax),
    xScale,
    nPoints,
  );
  return curvePathFrom(points, xScale, yScale);
}

//#endregion

//#region d3 shell append helpers

// Ticks-only background for the inline (scaled, no grid labels) mode — the inline counterpart to
// appendFreqEditorGrid below. Both live here so every frequency-domain chart (response curves,
// bands) mounts the exact same background regardless of what it draws on top.
function appendInlineTicks(
  svg: SvgSelection,
  opts: FreqSvgOpts,
  width: number,
  height: number,
  pad: number,
): void {
  const tx = makeTx(width, pad, opts["domain/fMin"], opts["domain/fMax"]);
  svg.append("g").selectAll("line")
    .data(opts["domain/ticks"])
    .enter()
    .append("line")
    .attr("x1", (freq) => tx(freq))
    .attr("y1", 0)
    .attr("x2", (freq) => tx(freq))
    .attr("y2", height)
    .attr("stroke", "#1e1e1e")
    .attr("stroke-width", 1);
}

// Mounts the svg shell shared by every frequency-domain chart (frequency-response-curves,
// frequency-bands): fixed-size crosshair svg + full tick/label grid for "editor" mode, or a
// scaled-to-fit svg + plain background + bare tick lines for "inline" mode. Consumers append
// their own curve/band/handle marks on top — this is only the background, not a whole chart.
export function mountFreqChartSvg(
  container: HTMLDivElement,
  opts: FreqSvgOpts,
  mode: "editor" | "inline",
): { svg: SvgSelection; width: number; height: number; pad: number } {
  const width = mode === "editor" ? opts["editor/width"] : opts["inline/virtualWidth"];
  const height = mode === "editor" ? opts["editor/height"] : opts["inline/height"];
  const pad = mode === "editor" ? opts["editor/pad"] : opts["inline/pad"];

  const svg = mode === "editor"
    ? appendFixedSvg(container, width, height)
    : appendScaledSvg(container, width, height);

  if (mode === "editor") {
    appendFreqEditorGrid(svg, opts);
  } else {
    appendBackgroundRect(svg, width, height);
    appendInlineTicks(svg, opts, width, height, pad);
  }

  return { svg, width, height, pad };
}

// Draws the frequency-editor background grid (tick lines + labels + the 0dB/unity dashed
// reference line) directly into a d3-owned svg — replaces the old FreqSvgEditor JSX shell.
// Call once during a consumer's chart init, before appending its own curve/handle marks.
export function appendFreqEditorGrid(
  svg: SvgSelection,
  opts: FreqSvgOpts,
): void {
  const svgWidth = opts["editor/width"];
  const svgHeight = opts["editor/height"];
  const pad = opts["editor/pad"];
  const tx = makeTx(svgWidth, pad, opts["domain/fMin"], opts["domain/fMax"]);
  const vy = makeVy(svgHeight, pad, opts["domain/mMax"]);

  appendBackgroundRect(svg, svgWidth, svgHeight);

  const ticks = svg.append("g").selectAll("line")
    .data(opts["domain/ticks"])
    .enter()
    .append("line")
    .attr("x1", (freq) => tx(freq))
    .attr("y1", pad)
    .attr("x2", (freq) => tx(freq))
    .attr("y2", svgHeight - pad)
    .attr("stroke", "#1e1e1e")
    .attr("stroke-width", 1);
  void ticks;

  svg.append("line")
    .attr("x1", pad)
    .attr("y1", vy(1))
    .attr("x2", svgWidth - pad)
    .attr("y2", vy(1))
    .attr("stroke", "#2a2a2a")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2");

  svg.append("g").selectAll("text")
    .data(opts["domain/ticks"])
    .enter()
    .append("text")
    .attr("x", (freq) => tx(freq))
    .attr("y", svgHeight - 2)
    .attr("text-anchor", "middle")
    .attr("fill", "#444")
    .attr("font-size", 7)
    .style("pointer-events", "none")
    .text((freq) => freq >= 1000 ? `${freq / 1000}k` : `${freq}`);
}

//#endregion
